/**
 * Agent Policy — boundary contract schemas for agent enablement + review-gate
 * policy endpoints.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/agent-policy.routes.ts`
 * registers these schemas, and `apps/landlord`'s ListingDetailPage / lead-detail
 * OverviewTab derive their policy types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers:
 *   GET/PATCH /listings/:id/agent-policy
 *   GET/PATCH /leads/:id/agent-policy
 *   GET/PATCH /cases/:id/agent-policy   (BR-AGENT-POLICY-CASE-SCOPE)
 *   GET/PATCH /agent-policy/default/:agentKind (landlord-level, per agent kind)
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enum schemas — mirror AgentEnablementDecision / AgentReviewDecision
// ---------------------------------------------------------------------------
export const agentEnablementDecisionSchema = z.enum(['enabled', 'disabled', 'inherit']);
export const agentReviewDecisionSchema = z.enum(['required', 'not_required', 'inherit']);
// ---------------------------------------------------------------------------
// Policy source schema — which cascade level supplied the effective value
// ---------------------------------------------------------------------------
// BR-AGENT-POLICY-CASE-SCOPE: 'case' added alongside the service layer's PolicySource
// (apps/api/src/services/agent/agent-enablement.service.ts) — a case-scoped policy row's
// enablementSource/reviewSource can now resolve to 'case'.
// BR-101: 'subscription' = the landlord's plan/add-on does not entitle the agent; it overrides
// every policy row.
export const policySourceSchema = z.enum(['lead', 'listing', 'case', 'landlord', 'system', 'subscription']);
// ---------------------------------------------------------------------------
// Request body — at least one field required
// ---------------------------------------------------------------------------
export const patchAgentPolicyBodySchema = z
    .object({
    enablement: agentEnablementDecisionSchema
        .optional()
        .describe('Agent enablement tri-state: enabled | disabled | inherit'),
    review: agentReviewDecisionSchema
        .optional()
        .describe('Review-gate tri-state: required | not_required | inherit'),
})
    .refine((data) => data.enablement !== undefined || data.review !== undefined, { message: 'At least one of enablement or review must be provided' })
    .describe('Partial update to an agent policy scope (at least one field required)');
// ---------------------------------------------------------------------------
// Scope-id path param schema (shared by listing + lead policy routes)
// ---------------------------------------------------------------------------
export const agentPolicyScopeIdParamsSchema = z
    .object({
    id: z.uuid().describe('Scope ID (listing or lead UUID)'),
})
    .describe('Scope ID path parameter');
// ---------------------------------------------------------------------------
// Landlord-default route param — the agent kind (BR-POLICY-PER-AGENT)
// ---------------------------------------------------------------------------
/**
 * Agent kinds with a landlord-level policy row. Wider than playbookAgentKindSchema: the reference
 * coordinator has an on/off switch (enablement only — its review is always not_required, BR-28)
 * but no playbook, so the playbook routes keep rejecting it.
 */
export const policyAgentKindSchema = z
    .enum(['repair', 'leasing', 'reference_coordinator'])
    .describe('Agent kind with a landlord-level policy: repair | leasing | reference_coordinator');
export const agentPolicyDefaultParamsSchema = z
    .object({
    agentKind: policyAgentKindSchema,
})
    .describe('Landlord-default agent policy path parameter');
// ---------------------------------------------------------------------------
// Response — stored tri-state values + cascade-resolved effective values
// ---------------------------------------------------------------------------
export const agentPolicyResponseSchema = z
    .object({
    // Stored tri-state (can be 'inherit')
    enablement: agentEnablementDecisionSchema.describe("Stored enablement decision for this scope (can be 'inherit' — use resolvedEnablement for the effective value)"),
    review: agentReviewDecisionSchema.describe("Stored review-gate decision for this scope (can be 'inherit' — use resolvedReview for the effective value)"),
    // Resolved values — never 'inherit', always the actual effective value after cascade
    resolvedEnablement: z.enum(['enabled', 'disabled']).describe('Effective enablement after cascade resolution — never "inherit"'),
    resolvedReview: z.enum(['required', 'not_required']).describe('Effective review-gate after cascade resolution — never "inherit"'),
    enablementSource: policySourceSchema.describe('Which scope provided the effective enablement value (lead|listing|case|landlord|system|subscription)'),
    reviewSource: policySourceSchema.describe('Which scope provided the effective review value (lead|listing|case|landlord|system|subscription)'),
    agentEntitled: z.boolean().optional().describe('BR-101 — listing/lead scopes only: whether the landlord is entitled to the leasing agent here (current paid period or active add-on). Absent for case/landlord scopes.'),
})
    .describe('Agent policy state — stored values plus resolved effective values after cascade');
//# sourceMappingURL=policy.js.map